import React, { useState } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';

declare global {
  interface Window {
    ethereum?: any;
  }
}

const PaymentButton: React.FC = () => {
  const [transactionStatus, setTransactionStatus] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>('start.png'); // Placeholder image
  const [prompt, setPrompt] = useState<string>('A close-up of a human eye with intricate details and reflections, capturing the complexity of the iris.');
  const [model, setModel] = useState<string>('stability-ai/stable-diffusion-3');

  const handlePayment = async () => {
    console.log('handlePayment function called');

    if (window.ethereum) {
      console.log('Ethereum object found');
      setTransactionStatus('Requesting account access...');

      try {
        // Request account access
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        console.log('Account access requested');
        setTransactionStatus('Account access granted');

        // Switch to Conwai Testnet if not already connected
        const chainId = '0x3CE0B8'; // Chain ID for Conwai Testnet in hexadecimal
        const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
        console.log('Current Chain ID:', currentChainId);

        if (currentChainId !== chainId) {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId }],
          });
          console.log('Switched to Conwai Testnet');
          setTransactionStatus('Switched to Conwai Testnet');
        }

        // Create a new Web3 provider
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        console.log('Web3 provider created', provider);

        // Get the signer
        const signer = provider.getSigner();
        console.log('Signer obtained:', signer);

        // Define the transaction parameters
        const tx = {
          to: '0xC665A725208d4665A367BaCceaAB638Aa7E092D7', // Replace with the recipient address
          value: ethers.utils.parseEther('0.01'), // Replace with the amount to send in CWT
        };

        // Log the transaction details
        console.log('Transaction details:', tx);
        setTransactionStatus('Sending transaction...');

        // Send the transaction
        const transactionResponse = await signer.sendTransaction(tx);
        console.log('Transaction Response:', transactionResponse);
        setTransactionStatus('Transaction sent, waiting for confirmation...');

        // Wait for the transaction to be mined
        await transactionResponse.wait();
        console.log('Transaction confirmed');
        setTransactionStatus('Transaction confirmed');

        // Define the API call based on the selected model
        let apiEndpoint = '';
        switch (model) {
          case 'stability-ai/stable-diffusion-3':
            apiEndpoint = 'stability-ai/stable-diffusion-3';
            break;
          case 'ssd-1b':
            apiEndpoint = 'lucataco/ssd-1b:b19e3639452c59ce8295b82aba70a231404cb062f2eb580ea894b31e8ce5bbb6';
            break;
          case 'sticker-maker':
            apiEndpoint = 'fofr/sticker-maker:4acb778eb059772225ec213948f0660867b2e03f277448f18cf1800b96a65a1a';
            break;
          default:
            apiEndpoint = 'stability-ai/stable-diffusion-3';
        }

        // Call backend server to create image
        const response = await axios.post('https://replicate-backend-opal.vercel.app/create-image', {
          prompt,
          aspect_ratio: '3:2',
          model: apiEndpoint,
        });
        console.log('Image creation response:', response.data);
        setTransactionStatus('Image created');
        setImageUrl(response.data.output[0]); // Assuming the URL is in the first element of the output array

      } catch (error: any) {
        console.error('Error during transaction:', error);

        // If the user is not connected to the Conwai Testnet, add and switch to it
        if (error.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0x3CE0B8',
                chainName: 'Conwai Testnet',
                nativeCurrency: {
                  name: 'CWT',
                  symbol: 'CWT',
                  decimals: 18,
                },
                rpcUrls: ['https://conwai-testnet-0.rpc.caldera.xyz/http'],
                blockExplorerUrls: ['https://conwai-testnet-0.explorer.caldera.xyz/'],
              }],
            });
            console.log('Added Conwai Testnet to MetaMask');
            setTransactionStatus('Added Conwai Testnet to MetaMask, retrying payment');

            // Retry the payment after adding the chain
            handlePayment();
          } catch (addError: any) {
            console.error('Failed to add and switch to Conwai Testnet', addError);
            setTransactionStatus('Failed to add and switch to Conwai Testnet');
          }
        } else {
          setTransactionStatus('Error during transaction');
        }
      }
    } else {
      console.error('MetaMask is not installed');
      setTransactionStatus('MetaMask is not installed');
    }
  };

  return (
    <div
      style={{
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        textAlign: 'center',
        padding: '40px',
        background: 'linear-gradient(135deg, #2E3440, #4C566A)',
        color: '#ECEFF4',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <header style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#88C0D0', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
          PrismaForge
        </h1>
        <nav style={{ marginBottom: '30px' }}>
          <ul
            style={{
              listStyleType: 'none',
              padding: 0,
              display: 'flex',
              justifyContent: 'center',
              borderBottom: '2px solid #4C566A',
              fontSize: '1.2rem',
            }}
          >
            <li style={{ margin: '0 20px' }}>
              <a
                href="#home"
                style={{
                  textDecoration: 'none',
                  color: '#8FBCBB',
                  padding: '10px 20px',
                  display: 'block',
                  borderBottom: '2px solid transparent',
                  transition: 'border-bottom 0.3s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.borderBottom = '2px solid #8FBCBB')}
                onMouseOut={(e) => (e.currentTarget.style.borderBottom = '2px solid transparent')}
              >
                Home
              </a>
            </li>
            <li style={{ margin: '0 20px' }}>
              <a
                href="#about"
                style={{
                  textDecoration: 'none',
                  color: '#8FBCBB',
                  padding: '10px 20px',
                  display: 'block',
                  borderBottom: '2px solid transparent',
                  transition: 'border-bottom 0.3s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.borderBottom = '2px solid #8FBCBB')}
                onMouseOut={(e) => (e.currentTarget.style.borderBottom = '2px solid transparent')}
              >
                About
              </a>
            </li>
            <li style={{ margin: '0 20px' }}>
              <a
                href="#contact"
                style={{
                  textDecoration: 'none',
                  color: '#8FBCBB',
                  padding: '10px 20px',
                  display: 'block',
                  borderBottom: '2px solid transparent',
                  transition: 'border-bottom 0.3s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.borderBottom = '2px solid #8FBCBB')}
                onMouseOut={(e) => (e.currentTarget.style.borderBottom = '2px solid transparent')}
              >
                Contact
              </a>
            </li>
          </ul>
        </nav>
      </header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handlePayment();
        }}
        style={{
          marginBottom: '30px',
          padding: '20px',
          background: '#3B4252',
          borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%',
        }}
      >
        <label style={{ display: 'block', marginBottom: '20px' }}>
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            style={{
              padding: '12px',
              width: '100%',
              borderRadius: '8px',
              border: '2px solid #4C566A',
              fontSize: '16px',
              color: '#ECEFF4',
              background: '#434C5E',
              outline: 'none',
              transition: 'border-color 0.3s',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#88C0D0')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#4C566A')}
          />
        </label>
        <label style={{ display: 'block', marginBottom: '20px' }}>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            style={{
              padding: '12px',
              width: '100%',
              borderRadius: '8px',
              border: '2px solid #4C566A',
              fontSize: '16px',
              color: '#ECEFF4',
              background: '#434C5E',
              outline: 'none',
              transition: 'border-color 0.3s',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#88C0D0')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#4C566A')}
          >
            <option value="stability-ai/stable-diffusion-3">Stable Diffusion 3</option>
            <option value="ssd-1b">SSD-1B</option>
            <option value="sticker-maker">Sticker Maker</option>
          </select>
        </label>
        <button
          type="submit"
          style={{
            padding: '12px 24px',
            backgroundColor: '#88C0D0',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.3s, transform 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#81A1C1')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#88C0D0')}
          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          0.01 CWT
        </button>
      </form>
      {transactionStatus && (
        <p
          style={{
            fontSize: '1.2rem',
            margin: '20px 0',
            padding: '10px',
            backgroundColor: '#3B4252',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            width: '100%',
          }}
        >
          {transactionStatus}
        </p>
      )}
      {imageUrl && (
        <img
          src={imageUrl}
          alt="Generated"
          style={{
            maxWidth: '90%',
            height: 'auto',
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      )}
    </div>
  );
};

export default PaymentButton;
